/**
 * WebSocket Client for Sportsbook Realtime Updates
 * 
 * Handles connection to the realtime server, automatic reconnection,
 * and message routing to subscription handlers.
 */

class SportsbookWebSocketClient {
  constructor() {
    this.ws = null;
    this.url = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.reconnectTimer = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.listeners = new Map();
    this.messageQueue = [];
    this.heartbeatInterval = null;
    this.connectionStatusCallbacks = [];
  }

  /**
   * Get WebSocket URL based on environment
   */
  getWebSocketUrl() {
    // Check for explicit env var first
    if (process.env.REACT_APP_REALTIME_WS_URL) {
      return process.env.REACT_APP_REALTIME_WS_URL;
    }

    // Determine URL based on current hostname
    // Use ws:// for local development (.caribe7.local), wss:// for dev/prod
    // Connect to sportsbook-realtime.{domain} whatever domain is
    const hostname = window.location.hostname;
    
    // Extract domain from hostname (e.g., "sports.caribe7.local" -> "caribe7.local")
    const domainMatch = hostname.match(/\.(.+)$/);
    const domain = domainMatch ? domainMatch[1] : hostname;
    
    // Use ws:// for local development, wss:// for dev/prod
    const isLocal = domain.includes('caribe7.local');
    const protocol = isLocal ? 'ws' : 'wss';
    
    // Build WebSocket URL: sportsbook-realtime.{domain}
    return `${protocol}://sportsbook-realtime.${domain}`;
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.isConnecting || (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;
    this.url = this.getWebSocketUrl();

    console.log(`[Realtime] Connecting to ${this.url}...`);

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('[Realtime] ✅ Connected');
        this.isConnecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.notifyConnectionStatus(true);

        // Start heartbeat
        this.startHeartbeat();

        // Send queued messages
        this.flushMessageQueue();

        // Notify listeners
        this.emit('connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('[Realtime] Error parsing message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[Realtime] 🔌 Connection closed', event.code, event.reason);
        this.isConnecting = false;
        this.isConnected = false;
        this.notifyConnectionStatus(false);
        this.stopHeartbeat();

        // Attempt reconnect if not a clean close
        if (event.code !== 1000) {
          this.attemptReconnect();
        }

        this.emit('disconnected', { code: event.code, reason: event.reason });
      };

      this.ws.onerror = (error) => {
        console.error('[Realtime] ❌ WebSocket error:', error);
        this.isConnecting = false;
        this.notifyConnectionStatus(false);
        this.emit('error', error);
      };

    } catch (error) {
      console.error('[Realtime] ❌ Failed to create WebSocket:', error);
      this.isConnecting = false;
      this.notifyConnectionStatus(false);
      this.attemptReconnect();
    }
  }

  /**
   * Handle incoming message
   */
  handleMessage(message) {
    const { type } = message;

    switch (type) {
      case 'CONNECTED':
        console.log('[Realtime] Server confirmed connection');
        break;

      case 'UPDATE':
        // Route to update handler
        this.emit('update', message);
        break;

      case 'ERROR':
        console.error('[Realtime] Server error:', message.message);
        this.emit('error', new Error(message.message));
        break;

      case 'PONG':
        // Heartbeat response
        break;

      default:
        console.warn('[Realtime] Unknown message type:', type);
    }
  }

  /**
   * Send message to server
   */
  send(message) {
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
      } catch (error) {
        console.error('[Realtime] Error sending message:', error);
      }
    } else {
      // Queue message for when connection is established
      this.messageQueue.push(message);
    }
  }

  /**
   * Flush queued messages
   */
  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  /**
   * Subscribe to entity updates
   */
  subscribe(entityType, ids) {
    if (!Array.isArray(ids) || ids.length === 0) {
      return;
    }

    const message = {
      type: `SUBSCRIBE_${entityType.toUpperCase()}`,
      ids: ids
    };

    this.send(message);
  }

  /**
   * Unsubscribe from entity updates
   */
  unsubscribe(entityType, ids) {
    if (!Array.isArray(ids) || ids.length === 0) {
      return;
    }

    const message = {
      type: `UNSUBSCRIBE_${entityType.toUpperCase()}`,
      ids: ids
    };

    this.send(message);
  }

  /**
   * Attempt to reconnect
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[Realtime] Max reconnection attempts reached');
      this.notifyConnectionStatus(false);
      return;
    }

    if (this.reconnectTimer) {
      return; // Already scheduled
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, this.maxReconnectDelay);

    console.log(`[Realtime] 🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'PING' });
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Disconnect from server
   */
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();
    this.messageQueue = [];

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.notifyConnectionStatus(false);
  }

  /**
   * Event emitter methods
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) {
      return;
    }
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.listeners.has(event)) {
      return;
    }
    this.listeners.get(event).forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[Realtime] Error in ${event} listener:`, error);
      }
    });
  }

  /**
   * Register connection status callback
   */
  onConnectionStatusChange(callback) {
    this.connectionStatusCallbacks.push(callback);
  }

  /**
   * Notify connection status change
   */
  notifyConnectionStatus(connected) {
    this.connectionStatusCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('[Realtime] Error in connection status callback:', error);
      }
    });
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// Export singleton instance
const realtimeClient = new SportsbookWebSocketClient();

export default realtimeClient;

